import { Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MdOutlineMap } from 'react-icons/md';
import { Input } from '@ds';
import { SSchedulerSettingsInputsFormType } from '@lp/features/Scheduler/SchedulerSurgeonsSettings/types';
import { getNumberInputValue, getNumberInputValueHandler } from '@lp/features/Scheduler/utils';
import { SRegion } from '@/api/apis/resources/schedule';
import { getKeys } from '@/utils/helpers';
import { OrganType, OrganTypeWithoutKidney } from '@/api/apis/case';

// TMDXLP-14091: Kidneys hidden from RS (Surgeons) Settings table below. Revert OrganTypeWithoutKidney -> OrganType to restore.
const organsList = getKeys(OrganTypeWithoutKidney) as OrganType[];

const comboRuleTypeList = ['strict', 'flex'] as const;

type SSchedulerSettingsInputsFormProps = {
  regionList: SRegion[];
};

export const SSchedulerSettingsInputsForm = ({ regionList }: SSchedulerSettingsInputsFormProps) => {
  const { control } = useFormContext<SSchedulerSettingsInputsFormType>();

  return (
    <div>
      <p className="mb-4 text-xl-sb text-base-5">Minimum Retrieval Surgeon On Call Everyday</p>
      <div className="flex gap-4">
        <div className="w-full min-w-[60%] p-4 bg-white rounded-2xl shadow-s">
          <table className="w-full">
            <thead>
              <tr className="first:[&>th]:rounded-s-lg last:[&>th]:rounded-e-lg">
                <th aria-label="Placeholder" className="bg-base-70" />
                {organsList.map((organ) => (
                  <th
                    key={organ}
                    aria-colspan={2}
                    colSpan={2}
                    className="h-10 w-20 px-3 bg-base-70 text-s-sb text-base-20 text-end"
                  >
                    {organ}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="h-7 px-3 text-base-25 text-s-r">Required min value:</td>
                {organsList.map((organ) => (
                  <Fragment key={organ}>
                    {comboRuleTypeList.map((comboType) => (
                      <th key={comboType} className="px-3 text-s-r text-base-25 text-end capitalize">
                        {comboType}
                      </th>
                    ))}
                  </Fragment>
                ))}
              </tr>
              {regionList.map((region) => (
                <tr key={region.id} className="odd:bg-base-90 first:[&>td]:rounded-s-lg last:[&>td]:rounded-e-lg">
                  <td className="h-[57px] px-3 py-2 text-m-m text-base-5">
                    <div className="flex items-center gap-2">
                      <MdOutlineMap size={20} />
                      <span>{region.name}</span>
                    </div>
                  </td>
                  {organsList.map((organ) => (
                    <Fragment key={organ}>
                      {comboRuleTypeList.map((comboType) => (
                        <td key={comboType} className="w-20 px-3 py-2 text-m-m text-base-5">
                          {region.organs.includes(organ) && (
                            <Controller
                              name={`perRegions.${region.id}.${organ}.${comboType}`}
                              control={control}
                              render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                                <Input
                                  type="number"
                                  min={0}
                                  isInvalid={invalid}
                                  errorMessage={error?.message}
                                  value={getNumberInputValue(value)}
                                  onValueChange={getNumberInputValueHandler(onChange)}
                                  {...field}
                                />
                              )}
                            />
                          )}
                        </td>
                      ))}
                    </Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-4">
          <ul className="list-outside list-disc text-base-10 text-s-r">
            <li className="mb-5">
              <b>Strict Value:</b> The algorithm will achieve the entered value. However, setting it too high may result
              in the algorithm being unable to generate a schedule due to staffing limitations. This value helps reduce
              the algorithm's processing time by narrowing the focus.
            </li>
            <li>
              <b>Flex Value:</b> This value represents a desired goal. The algorithm will attempt to approximate this
              target when constructing the schedule, although achieving it is not guaranteed. This value will help the
              algorithm build an optimized schedule.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
